import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Warehouse,
  ReceiptText,
  ShoppingCart,
  Users,
  BarChart3,
  ArrowLeftRight,
  FileSpreadsheet,
  HelpCircle,
  Boxes,
  Clock3,
  CheckCircle2,
} from "lucide-react";
import Layout from "src/components/layout/website/Layout";
import Button from "src/components/ui/Button";
import { PAGE_SEO } from "src/constants/seo";
import { trackCta, trackNavClick } from "src/utils/analytics";

const TRUST_CHIPS = [
  "GST-ready billing",
  "Multi-warehouse from day one",
  "Free 15-day trial, no card needed",
];

const PAIN_POINTS = [
  {
    title: "Spreadsheets that don't add up",
    desc: "One tab for stock, another for sales, a third someone forgot to update. By the time you reconcile it, the numbers are already stale.",
    Icon: FileSpreadsheet,
  },
  {
    title: "Stock counts that are a guess",
    desc: "You find out something's out of stock when a customer asks — not before. Multiple locations make it worse, not better.",
    Icon: Boxes,
  },
  {
    title: "GST billing eating your evening",
    desc: "Manually calculating tax on every invoice, then hoping the numbers match at filing time. There's a better way to spend that hour.",
    Icon: ReceiptText,
  },
  {
    title: "No one knows who did what",
    desc: "A price got changed, an order got edited — and there's no record of who, when, or why. Not great when you need to trace it back.",
    Icon: Clock3,
  },
];

const FEATURES = [
  {
    title: "Real-time stock, every warehouse",
    desc: "See exact quantities across every location the moment they change — no end-of-day reconciliation required.",
    Icon: Warehouse,
  },
  {
    title: "GST-ready billing",
    desc: "Tax calculated correctly on every sale, pulled from the product's own GST rate — not re-typed and hoped for.",
    Icon: ReceiptText,
  },
  {
    title: "Purchases that update stock automatically",
    desc: "Record a purchase order and the stock is in the right warehouse immediately — no separate 'receive' step to remember.",
    Icon: ShoppingCart,
  },
  {
    title: "Stock transfers, tracked end to end",
    desc: "Move inventory between warehouses with a full record of what went where, and when.",
    Icon: ArrowLeftRight,
  },
  {
    title: "Role-based access for your team",
    desc: "Give staff exactly the access their role needs — nothing more — as your team grows past just you.",
    Icon: Users,
  },
  {
    title: "Reports that explain what happened",
    desc: "Sales, purchases, stock, and profit — filtered by date and warehouse, exportable when you need to share them.",
    Icon: BarChart3,
  },
];

const FAQ_PREVIEW = [
  {
    q: "Is there a free trial?",
    a: "Yes — every new account starts on a free 15-day Trial plan with full access to core features. No card required.",
  },
  {
    q: "Can I manage more than one warehouse?",
    a: "Yes. Multi-warehouse support is built in from the start, not bolted on as an add-on — including stock transfers between locations.",
  },
  {
    q: "Is GST billing included?",
    a: "Yes, on every plan. Tax is calculated from each product's own GST rate on every sale and purchase.",
  },
];

const HomePage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Layout
      meta={PAGE_SEO.home}
    >
      <div className="bg-white text-gray-800">
        {/* Hero */}
        <section className="relative overflow-hidden bg-gradient-to-b from-primary-50/70 via-white to-white">
          <div className="max-w-7xl mx-auto px-6 pt-16 pb-20 md:pt-24 md:pb-28 grid md:grid-cols-2 gap-14 items-center">
            <div>
              <span className="inline-flex items-center rounded-full border border-primary-200 bg-white px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wide text-primary-700">
                Inventory · GST billing · Multi-warehouse
              </span>
              <h1 className="mt-5 text-4xl md:text-5xl font-extrabold leading-[1.1] tracking-tight text-gray-900">
                Stock, billing, and purchases —{" "}
                <span className="text-primary-600">finally talking to each other</span>
              </h1>
              <p className="mt-5 text-lg text-gray-600 max-w-lg">
                Invyntra keeps every warehouse, sale, and purchase order in
                sync in real time, with GST-ready billing built in. Built for
                small and growing Indian businesses ready to leave
                spreadsheets behind.
              </p>

              <div className="mt-7 flex flex-wrap gap-2.5">
                {TRUST_CHIPS.map((chip) => (
                  <span
                    key={chip}
                    className="inline-flex items-center gap-1.5 rounded-full bg-gray-50 border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600"
                  >
                    <CheckCircle2 size={13} className="text-primary-500" />
                    {chip}
                  </span>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Button
                  size="lg"
                  onClick={() => {
                    trackCta("Start Free Trial", "hero");
                    navigate("/app/register");
                  }}
                >
                  Start Free Trial
                  <ArrowRight size={17} className="ml-1.5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => {
                    trackCta("Talk to Us", "hero");
                    navigate("/contact");
                  }}
                >
                  Talk to Us
                </Button>
              </div>
              <p className="mt-4 text-sm text-gray-400">
                Early access — we personally set up every new account.{" "}
                <Link
                  to="/pricing"
                  onClick={() => trackNavClick("See pricing", "hero")}
                  className="text-primary-600 font-medium"
                >
                  See pricing
                </Link>
              </p>
            </div>

            {/* Composed dashboard mockup — not a literal screenshot, so it
                stays sharp at any size and doesn't date as fast as the real
                UI evolves. */}
            <div className="relative">
              <div className="absolute -inset-6 -z-10 rounded-[2rem] bg-primary-100/60 blur-2xl" />
              <div className="rounded-2xl border border-gray-200 bg-white shadow-2xl shadow-primary-900/10 overflow-hidden">
                <div className="flex items-center gap-1.5 border-b border-gray-100 bg-gray-50 px-4 py-2.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-gray-300" />
                  <span className="h-2.5 w-2.5 rounded-full bg-gray-300" />
                  <span className="h-2.5 w-2.5 rounded-full bg-gray-300" />
                  <span className="ml-2 text-[11px] text-gray-400">invyntra.in/app/dashboard</span>
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-5">
                    <div>
                      <p className="text-[11px] text-gray-400">Welcome back</p>
                      <p className="text-sm font-semibold text-gray-900">Aurora Retail Co.</p>
                    </div>
                    <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-medium text-emerald-600">
                      All warehouses synced
                    </span>
                  </div>

                  <div className="grid grid-cols-3 gap-3 mb-4">
                    {[
                      ["Products", "1,248"],
                      ["Stock value", "₹18.4L"],
                      ["Low stock", "6"],
                    ].map(([label, value]) => (
                      <div key={label} className="rounded-xl border border-gray-100 bg-gray-50 p-3">
                        <p className="text-[10px] text-gray-400">{label}</p>
                        <p className="text-base font-bold text-gray-900 mt-0.5">{value}</p>
                      </div>
                    ))}
                  </div>

                  <div className="rounded-xl border border-gray-100 p-3.5">
                    <div className="flex items-center justify-between mb-2.5">
                      <p className="text-[11px] font-medium text-gray-500">Sales, last 7 days</p>
                      <p className="text-[11px] text-primary-600 font-medium">+18%</p>
                    </div>
                    <div className="flex items-end gap-1.5 h-16">
                      {[38, 55, 46, 70, 50, 82, 64].map((h, i) => (
                        <div
                          key={i}
                          style={{ height: `${h}%` }}
                          className={`flex-1 rounded-t ${
                            i === 5 ? "bg-primary-600" : "bg-primary-200"
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="hidden sm:block absolute -right-6 top-10 rounded-xl border border-gray-100 bg-white shadow-lg px-3.5 py-2.5">
                <p className="text-[11px] font-medium text-gray-700">GST invoice ready</p>
                <p className="text-[10px] text-gray-400">Tax calculated automatically</p>
              </div>
              <div className="hidden sm:block absolute -left-6 bottom-8 rounded-xl border border-gray-100 bg-white shadow-lg px-3.5 py-2.5">
                <p className="text-[11px] font-medium text-gray-700">3 warehouses</p>
                <p className="text-[10px] text-gray-400">Live stock across all of them</p>
              </div>
            </div>
          </div>
        </section>

        {/* Pain points */}
        <section className="py-20 px-6 bg-gray-50/70 border-y border-gray-100">
          <div className="max-w-6xl mx-auto">
            <div className="max-w-2xl mb-12">
              <p className="text-xs font-semibold uppercase tracking-wide text-primary-600 mb-2">
                Sound familiar?
              </p>
              <h2 className="text-3xl font-bold text-gray-900">
                Still running inventory the hard way?
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              {PAIN_POINTS.map((p) => (
                <div
                  key={p.title}
                  className="flex gap-4 rounded-2xl bg-white border border-gray-100 p-5"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
                    <p.Icon size={19} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{p.title}</h3>
                    <p className="text-sm text-gray-500 leading-relaxed">{p.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-20 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="max-w-2xl mb-12">
              <p className="text-xs font-semibold uppercase tracking-wide text-primary-600 mb-2">
                What you get
              </p>
              <h2 className="text-3xl font-bold text-gray-900 mb-3">
                Everything a growing operation actually needs
              </h2>
              <p className="text-gray-500">
                Not a stock counter with a spreadsheet bolted on — the daily
                operations of running a real, growing business.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {FEATURES.map((f) => (
                <div
                  key={f.title}
                  className="rounded-2xl border border-gray-100 p-6 hover:border-primary-200 hover:shadow-sm transition"
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-50 text-primary-600 mb-4">
                    <f.Icon size={20} />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1.5">{f.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Honest early-access positioning — no fabricated stats or
            testimonials while there isn't yet a real customer base to cite. */}
        <section className="py-20 px-6 bg-primary-600">
          <div className="max-w-5xl mx-auto text-center">
            <p className="text-xs font-semibold uppercase tracking-wide text-primary-200 mb-3">
              Early access
            </p>
            <h2 className="text-3xl font-bold text-white mb-4">
              We set up every new account ourselves
            </h2>
            <p className="text-primary-100 max-w-2xl mx-auto mb-10">
              Invyntra is early — which means when you sign up, you're not
              talking to a support queue, you're talking to the people
              building it. We'll walk through your products, warehouses, and
              team with you, and shape what we build next around what
              businesses like yours actually need.
            </p>
            <div className="grid sm:grid-cols-3 gap-6 text-left max-w-3xl mx-auto">
              {[
                ["Personal onboarding", "We set up your first warehouse and catalog with you, not a help article."],
                ["Direct access to us", "Questions go straight to the team building the product — not a ticket queue."],
                ["Pricing that won't move under you", "What you sign up for is what you pay — no surprise tier changes."],
              ].map(([title, desc]) => (
                <div key={title} className="rounded-2xl bg-primary-500/40 border border-primary-400/40 p-5">
                  <h3 className="font-semibold text-white mb-1.5">{title}</h3>
                  <p className="text-sm text-primary-100 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ preview */}
        <section className="py-20 px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-xs font-semibold uppercase tracking-wide text-primary-600 mb-2">
                Common questions
              </p>
              <h2 className="text-3xl font-bold text-gray-900">
                Before you start
              </h2>
            </div>
            <div className="space-y-4">
              {FAQ_PREVIEW.map((item) => (
                <div key={item.q} className="rounded-2xl border border-gray-100 p-5">
                  <div className="flex items-start gap-3">
                    <HelpCircle size={18} className="text-primary-500 shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">{item.q}</h3>
                      <p className="text-sm text-gray-500">{item.a}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link
                to="/faqs"
                onClick={() => trackNavClick("See all FAQs", "faq_preview")}
                className="text-sm font-medium text-primary-600"
              >
                See all FAQs →
              </Link>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 px-6 bg-gray-900">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to stop guessing what's in stock?
            </h2>
            <p className="text-gray-400 mb-8 max-w-xl mx-auto">
              Start your free 15-day trial — no credit card, and we'll help
              you get set up personally.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Button
                size="lg"
                onClick={() => {
                  trackCta("Start Free Trial", "bottom_cta");
                  navigate("/app/register");
                }}
              >
                Start Free Trial
                <ArrowRight size={17} className="ml-1.5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => {
                  trackCta("Talk to Us", "bottom_cta");
                  navigate("/contact");
                }}
              >
                Talk to Us
              </Button>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default HomePage;
